import React, { useState, useEffect } from 'react'
import { TrendingDown, TrendingUp, CreditCard as PaymentCard, Calendar as PaymentCalendar, Minus, ChartPie } from 'lucide-react'
import { runPaymentAnalysis } from '../api/requests';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../states/slices/resourceSlice';
import { BanknotesIcon } from '@heroicons/react/24/solid';
import moment from 'moment';
import { PaymetAnalysis } from '../resources/interfaces';
import { TbPlayerTrackNextFilled } from "react-icons/tb";


interface IPaymentAnalysis {
  data:PaymetAnalysis
}
interface IpaymentAnalysisDetails {
  paymentAnalysis?: PaymetAnalysis;
  balance?: number | string;
  financingApplicationNumber?: number | undefined;
  handleViewFullPayment?: () => void;
  handleRunPaymentAnalysis: () => void;
  currency?: string;
}



const PaymentAnalysis = ({ paymentAnalysis, balance, financingApplicationNumber, handleViewFullPayment, handleRunPaymentAnalysis,currency }: IpaymentAnalysisDetails) => {
  const country = useSelector(getSelectedCountry)

  return (
    <div>
      <div className="bg-white rounded-2xl shadow-md overflow-hidden dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 dark:bg-darkBg dark:border-darkBg-border">
          <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
            <ChartPie className='h-6 w-6 mr-2 text-gray-500 dark:text-darkBg-mainText'/>
            Payment Analysis
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 gap-5">
            {/* Payment History Summary */}
            <div>
              <p className="text-sm font-medium text-gray-500 mb-3">Payment History</p>
              {paymentAnalysis ? <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                <div className="text-center px-3">
                  <p className="text-xs text-gray-500">On Time</p>
                  <p className="text-lg font-bold text-green-600">{paymentAnalysis?.onTime}</p>
                </div>
                <div className="text-center px-3 border-l border-gray-200">
                  <p className="text-xs text-gray-500">Late</p>
                  <p className="text-lg font-bold text-amber-600">{paymentAnalysis?.late}</p>
                </div>
                <div className="text-center px-3 border-l border-gray-200">
                  <p className="text-xs text-gray-500">Missed</p>
                  <p className="text-lg font-bold text-red-600">{paymentAnalysis?.missed}</p>
                </div>
                <div className="text-center px-3 border-l border-gray-200">
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="text-lg font-bold text-blue-600">{paymentAnalysis?.total}</p>
                </div>
              </div> : <><div><p className='text-gray-300'>No history found</p></div></>}
            </div>

            {/* Payment Trend */}
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium text-gray-500">Payment Trend</p>
                {paymentAnalysis ? 
                <div className="flex items-center mt-1">
                   
                      <p className='text-xs'>{paymentAnalysis?.analysis}</p>
                    
                </div>
                 : 
                 <div><p className='text-gray-300'>Trend status not found </p></div>}
              </div>

              {paymentAnalysis && paymentAnalysis?.risk && 
              <div className={`px-3 py-2 whitespace-nowrap  rounded-lg 
              ${paymentAnalysis?.risk === 'Low' ? 'bg-green-100' : paymentAnalysis?.risk === 'Moderate' ? 'bg-amber-100' : 'bg-red-100'}`}>
                <span className={`text-sm font-medium ${paymentAnalysis?.risk === 'Low' ? 'text-green-800' : paymentAnalysis?.risk === 'Moderate' ? 'text-amber-800' : 'text-red-800'}`}>
                  {paymentAnalysis?.risk === 'Low' ? 'Low Risk' : paymentAnalysis?.risk === 'Moderate' ? 'Medium Risk' : 'High Risk'}
                </span>
              </div>}
            </div>

            {/* Credit Score */}
            {(
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Credit Score</p>
                {paymentAnalysis?.score ? <div className="relative pt-1">
                  <div className="flex mb-2 items-center justify-between">
                    <div>
                      <span className="text-lg font-semibold inline-block">
                        {paymentAnalysis?.score}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-semibold inline-block text-gray-600">
                        {paymentAnalysis?.score && paymentAnalysis?.score >= 90 ? 'Excellent' :
                          paymentAnalysis?.score && paymentAnalysis?.score >= 70 ? 'Good' :
                            paymentAnalysis?.score && paymentAnalysis?.score >= 50 ? 'Fair' :
                              paymentAnalysis?.score && paymentAnalysis?.score >= 30 ? 'Poor' : 'Very Poor'}
                      </span>
                    </div>
                  </div>
                  <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                    <div
                      style={{ width: `${paymentAnalysis?.score ? `${paymentAnalysis?.score}%` : '0%' }`}}
                      className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center 
                              ${paymentAnalysis?.score && paymentAnalysis?.score >= 90 ? 'bg-green-500' :
                          paymentAnalysis?.score && paymentAnalysis?.score >= 70 ? 'bg-green-400' :
                            paymentAnalysis?.score && paymentAnalysis?.score >= 50 ? 'bg-amber-400' :
                              paymentAnalysis?.score && paymentAnalysis?.score >= 30 ? 'bg-amber-500' : 'bg-red-500'}`}>
                    </div>
                  </div>
                </div> : <p className='text-gray-300'>No score available</p>}
              </div>
            )}

            {/* Payment Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Last Payment</p>
                <div className="mt-1">
                  <div className="flex items-center">
                    <PaymentCalendar className="h-4 w-4 text-gray-500 mr-1" />
                    <span className="text-sm text-gray-900 dark:text-darkBg-mainText font-medium">{paymentAnalysis?.lastPaidDate ? moment(paymentAnalysis?.lastPaidDate).format("YYYY-MM-DD") : '-'}</span>
                  </div>
                  <div className="flex items-center mt-1">
                    <PaymentCard className="h-4 w-4 text-gray-500 mr-1" />
                    <span className="text-sm text-gray-900 dark:text-darkBg-mainText font-medium">
                      {paymentAnalysis?.lastPaidAmount ?
                        country.currencySymbol + paymentAnalysis?.lastPaidAmount :
                        '-'}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Next Payment</p>
                <div className="mt-1">
                  <div className="flex items-center">
                    <PaymentCalendar className="h-4 w-4 text-gray-500 mr-1" />
                    <span className="text-sm text-gray-900 dark:text-darkBg-mainText font-medium">{paymentAnalysis?.nextPaymentDate ? moment( paymentAnalysis?.nextPaymentDate).format("DD-MM-YYYY") : '-'}</span>
                  </div>
                  <div className="flex items-center mt-1">
                    <PaymentCard className="h-4 w-4 text-gray-500 mr-1" />
                    <span className="text-sm text-gray-900 dark:text-darkBg-mainText font-medium">
                      {paymentAnalysis?.nextPaymentAmount ?
                        `${country.currencySymbol } ${paymentAnalysis?.nextPaymentAmount} ` :
                        '-'}
                    </span>
                  </div>
                </div>
              </div>
              {/* Outstanding Balance */}
              <div>
                <p className="text-sm font-medium text-gray-500">Outstanding Balance</p>
                <p className="text-lg font-bold text-gray-900 dark:text-darkBg-mainText mt-1">
                  {(paymentAnalysis?.outStandingAmount || paymentAnalysis?.outStandingAmount === 0) ? `${currency || 'GHS'} ${paymentAnalysis?.outStandingAmount}` : '-'}
                </p>
              </div>

              {/* Average Days Late */}
              <div>
                <p className="text-sm font-medium text-gray-500">Average Days Late</p>
                {
                  <p className="text-lg font-bold text-gray-900 dark:text-darkBg-mainText mt-1">
                    {(paymentAnalysis?.averageDaysLate || paymentAnalysis?.averageDaysLate === 0 )? `${paymentAnalysis?.averageDaysLate}` : '-'}
               
                  </p>}
              </div>
            </div>

            <div className="flex gap-4 pt-3 border-t border-gray-200 dark:border-darkBg-border">
              <button onClick={handleViewFullPayment} className="w-full px-4 py-2 bg-blue-50 text-blue-700 rounded-md text-sm font-medium hover:bg-blue-100 transition-colors flex items-center justify-center">
                <BanknotesIcon className="h-4 w-4 mr-2" />
                 History
              </button>
               <button onClick={handleRunPaymentAnalysis} className="w-full px-4 py-2 bg-blue-50 text-blue-700 rounded-md text-sm font-medium hover:bg-blue-100 transition-colors flex items-center justify-center">
                <TbPlayerTrackNextFilled className="h-4 w-4 mr-2" />
                Run
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentAnalysis